import CachedIcon from '@mui/icons-material/Cached';
import {
    IconButton as MUIIconButton,
    IconButtonProps as MUIIconButtonProps,
    SvgIconProps,
    Tooltip,
    TooltipProps,
} from '@mui/material';
import { ElementType } from 'react';

type PrimaryIconButtonProps = Omit<MUIIconButtonProps, 'children' | 'disabled'> & {
    isLoading?: boolean;
    tooltipTitle?: TooltipProps['title'];
    disabled?: boolean;
    icon: ElementType<SvgIconProps>;
};

type RefreshIconButtonProps = Omit<PrimaryIconButtonProps, 'icon'>;

const Primary = ({
    isLoading,
    tooltipTitle = 'Refresh',
    sx,
    disabled,
    icon: Icon,
    ...props
}: PrimaryIconButtonProps) => (
    <Tooltip title={tooltipTitle}>
        <span>
            <MUIIconButton
                {...props}
                color={props.color ?? 'white'}
                size={props.size ?? 'small'}
                disabled={disabled || isLoading}
                className={props.className ?? 'p-[3px] black-border'}
                sx={[
                    {
                        '&:not(.Mui-disabled)': {
                            bgcolor: 'primary-600.main',
                        },
                        '&:not(.Mui-disabled):hover': {
                            bgcolor: 'primary-700.main',
                        },
                    },
                    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                ]}
            >
                <Icon className={isLoading ? 'animate-spin' : undefined} />
            </MUIIconButton>
        </span>
    </Tooltip>
);

const Refresh = (props: RefreshIconButtonProps) => <Primary {...props} icon={CachedIcon} />;

const BaseIconButton = (props: MUIIconButtonProps) => <MUIIconButton {...props} />;

type IconButtonNamespace = ((props: MUIIconButtonProps) => JSX.Element) & {
    Primary: (props: PrimaryIconButtonProps) => JSX.Element;
    Refresh: (props: RefreshIconButtonProps) => JSX.Element;
};

const IconButtonNamespace = BaseIconButton as IconButtonNamespace;
IconButtonNamespace.Primary = Primary;
IconButtonNamespace.Refresh = Refresh;

export { IconButtonNamespace as IconButton };
export type { MUIIconButtonProps as IconButtonProps, PrimaryIconButtonProps, RefreshIconButtonProps };
